import { Children, type CSSProperties, type ReactNode } from "react";
import Tooltip, { type TooltipProps } from "@mui/material/Tooltip";
import cx from "classnames";
import WhiteTooltip from "../WhiteTooltip/WhiteTooltip";
import { useIsTruncated } from "./TruncationAwareTooltip.hooks";

interface TruncationAwareTooltipProps
  extends Omit<TooltipProps, "children" | "title"> {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  lines?: number;
  force?: boolean;
  title?: TooltipProps["title"];
  variant?: "light" | "dark";
}

const TruncationAwareTooltip = (allProps: TruncationAwareTooltipProps) => {
  const {
    children,
    lines = 1,
    className = "",
    style,
    force,
    variant = "dark",
    ...props
  } = allProps;
  props.title = Object.keys(allProps).includes("title") ? props.title : children;
  const truncateClassname = lines > 1 ? "line-clamp-1" : "truncate";

  const { textRef, isTruncated, checkTruncation } = useIsTruncated<HTMLDivElement>();

  // Ensure children is a single element, or wrap it in a fragment
  const wrappedChildren =
    Children.count(children) === 1 ? (
      children
    ) : (
      // biome-ignore lint/complexity/noUselessFragments: VALID USE CASE
      <>{children}</>
    );

  const Comp = variant === "light" ? WhiteTooltip : Tooltip;

  return (
    <Comp {...props} title={isTruncated || force ? props.title : ""}>
      <div
        ref={textRef}
        onMouseEnter={checkTruncation}
        className={cx(truncateClassname, className)}
        style={{
          WebkitLineClamp: lines,
          ...style,
        }}
      >
        {wrappedChildren}
      </div>
    </Comp>
  );
};

export default TruncationAwareTooltip;
